import { Button, Icon, MuiAlert, Spin, TextField, strIsNullOrEmpty, useDebounce } from '@fox/atoms';
import { DateFilter } from '@fox/data-grid';
import { FlexColumnSpaced } from '@fox/layout';
import { useMediaQuery } from '@mui/material';
import { useState } from 'react';
import styled from 'styled-components';
import { useContentContext } from '../../content-context';
import { useEvents, useRsvpOptions } from '../query-hooks/query-implementations';
import { EventCard } from './event-card';

const ControlsWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: 1rem;
	justify-content: center;
	flex-wrap: wrap;
	.filterControl {
		width: 180px;
		.MuiAutocomplete-root {
			height: 40px;
			min-height: 40px !important;
			.MuiFormControl-root {
				height: 40px;
				.MuiInputBase-root {
					height: 40px;
					.MuiInputBase-input {
						height: 20px;
					}
				}
			}
		}
	}
	@media (max-width: 600px) {
		padding: 0rem 1rem;
		flex-direction: column;
		.filterControl {
			width: 100%;
		}
	}
`;

export function Events() {
	const [search, setSearch] = useState<string>(undefined);
	const [startDate, setStartDate] = useState(undefined);
	const [endDate, setEndDate] = useState(undefined);
	const isMobile = useMediaQuery('(max-width: 800px)');

	const debouncedSearch = useDebounce(search, 500);
	const { contentId, requestToken } = useContentContext();
	const {
		data: eventsData,
		error: eventsError,
		loading: eventsLoading,
		refetch
	} = useEvents({
		contentId,
		requestToken,
		search: debouncedSearch,
		startDate: startDate ?? '@today',
		endDate,
		triggers: ['search', 'startDate', 'endDate']
	});
	const { data: rsvpOptions, error: rsvpOptionsError, loading: rsvpOptionsLoading } = useRsvpOptions({ contentId, requestToken });

	if (!strIsNullOrEmpty(eventsError)) return <MuiAlert severity="error">{eventsError}</MuiAlert>;
	if (!strIsNullOrEmpty(rsvpOptionsError)) return <MuiAlert severity="error">{rsvpOptionsError}</MuiAlert>;

	return (
		<Spin spinning={eventsLoading || rsvpOptionsLoading}>
			<ControlsWrapper>
				<div className="filterControl">
					<TextField
						value={search ?? ''}
						onChange={e => setSearch(e.target.value)}
						autoFocus
						sx={{ width: '100%' }}
						size="small"
						placeholder="Search"
						endAdornment={<Icon iconName="Search" />}
						inputProps={{
							style: { paddingLeft: 10, paddingRight: 15 }
						}}
					/>
				</div>
				<div className="filterControl">
					<DateFilter
						placeholder="Start date"
						width="100%"
						value={startDate}
						setValue={value => {
							setStartDate(value);
						}}
					/>
				</div>
				<div className="filterControl">
					<DateFilter placeholder="End date" width="100%" value={endDate} setValue={value => setEndDate(value)} />
				</div>
			</ControlsWrapper>
			<div style={{ textAlign: 'center', marginTop: '1rem' }}>
				<Button
					startIconName="Cancel"
					onClick={() => {
						setSearch(undefined);
						setEndDate(undefined);
						setStartDate(undefined);
					}}
					variant="secondary"
				>
					Reset filters
				</Button>
			</div>
			<FlexColumnSpaced>
				{eventsData?.events?.length ? (
					eventsData.events.map(event => {
						return <EventCard key={event.id} event={event} refetch={refetch} rsvpOptions={rsvpOptions} isMobile={isMobile} />;
					})
				) : (
					<MuiAlert sx={{ marginTop: '1rem' }} severity="info">
						There are no events to display
					</MuiAlert>
				)}
			</FlexColumnSpaced>
		</Spin>
	);
}
